import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import styles from '../NewLanding/NewLanding.module.css'; // Reusing the same styles initially
import Navbar from '../NewLanding/Navbar';
import PromoNotification from '../PromoNotification/PromoNotification';

// Reusing the MovingGridBackground component from NewLanding
const MovingGridBackground = () => {
  const ROWS = 6;
  const COLS = 4;
  const ITEMS_PER_ROW = COLS;
  
  const images = [
    '/images/grid/image_fx_ (4).jpg',
    '/images/grid/image_fx_ (5).jpg',
    '/images/grid/image_fx_ (6).jpg',
    '/images/grid/image_fx_ (7).jpg',
    '/images/grid/image_fx_ (8).jpg',
    '/images/grid/image_fx_ (9).jpg',
    '/images/grid/image_fx_ (10).jpg',
    '/images/grid/image_fx_ (11).jpg',
  ];
  
  const generateGridItems = () => {
    const rows = [];
    for (let i = 0; i < ROWS; i++) {
      const row = Array.from({ length: ITEMS_PER_ROW }, (_, j) => ({
        id: i * ITEMS_PER_ROW + j,
        rowIndex: i
      }));
      rows.push(row);
    }
    return rows;
  };

  const [offset, setOffset] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  const ANIMATION_SPEED = 100; // Slower movement
  const MAX_OFFSET = 100;

  useEffect(() => {
    const animationFrame = setInterval(() => {
      setOffset(prev => {
        const nextOffset = prev + direction;
        
        // Change direction when reaching limits
        if (nextOffset >= MAX_OFFSET) {
          setDirection(-1);
          return MAX_OFFSET;
        } else if (nextOffset <= 0) {
          setDirection(1);
          return 0;
        }
        
        return nextOffset;
      });
    }, ANIMATION_SPEED);
    
    return () => clearInterval(animationFrame);
  }, [direction]);

  return (
    <div className={styles.gridBackground}>
      <div className={styles.gridRotatedContainer}>
        <div className={styles.gridContainer}>
          {generateGridItems().map((row, rowIndex) => (
            <div 
              key={rowIndex}
              className={styles.gridRow}
              style={{
                transform: `translateX(${rowIndex % 2 === 0 ? -offset : offset}px)`,
                transition: 'transform 0.5s linear'
              }}
            >
              {row.map(item => (
                <div
                  key={item.id}
                  className={styles.gridItem}
                >
                  <img
                    src={images[item.id % images.length]}
                    alt={`Grid item ${item.id}`}
                    className={styles.gridImage}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.gridOverlay} />
    </div>
  );
};

const Affiliate = () => {
  const [promoVisible, setPromoVisible] = useState(true);
  
  const handlePromoDismiss = () => {
    setPromoVisible(false);
  };
  
  return (
    <div className={styles.container}>
      <Head>
        <title>Zenyai | Affiliate Program</title>
        <meta name="description" content="Join the Zenyai affiliate program and earn rewards by recommending our AI-powered audio platform" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <main className={styles.main}>
        <PromoNotificationWithCallback 
          visible={promoVisible} 
          onDismiss={handlePromoDismiss} 
        />
        <Navbar promoVisible={promoVisible} />
        
        <section className={styles.hero}>
          <MovingGridBackground />
          <div className={styles.heroContent} style={{ paddingTop: '0px', marginTop: '-120px' }}>
            <h1 className={styles.heroTitle}>
              <span className={styles.weAre}>Earn Rewards as a</span>
              <span className={styles.fullService}>Zenyai Affiliate</span>
            </h1>
            <div className={styles.heroDescription}>
              <p>Promote the world's leading AI-powered audio platform and earn generous commissions for every new user you bring on board.</p>
            </div>
            <div className={styles.buttonContainer}>
              <Link href="https://zenyai.promotekit.com" className={`${styles.button} ${styles.primaryButton}`}>
                Join Our Affiliate Program
              </Link>
              <Link href="#how-it-works" className={`${styles.button} ${styles.secondaryButton}`}>
                Learn More
              </Link>
            </div>
            
            {/* <div className={styles.quoteSection}>
              <div className={styles.quoteContainer}>
                <div className={styles.quoteSymbol}>&quot;</div>
                <blockquote className={styles.quote}>
                I earned over $2,000 in my first month as a Zenyai affiliate. The products practically sell themselves to audio professionals and game developers.
                </blockquote>
                <div className={styles.quoteAuthor}>
                  <p className={styles.authorName}>Michelle W.</p>
                  <p className={styles.authorTitle}>Audio Content Creator</p>
                </div>
              </div>
            </div> */}
          </div>
        </section>

        {/* Features Grid Section */}
        <section className={styles.featuresGrid} id="how-it-works">
          <div className={styles.featuresContainer}>
            <div className={styles.featureCard}>
              <div className={styles.featureCardBg}>
                <img 
                  src="/images/landing/Untitled design (4).png" 
                  alt="" 
                  className={styles.featureCardBgImage}
                />
              </div>
              
              <h3 className={styles.featureTitle}>How Our Affiliate Program Works</h3>
              <p className={styles.featureText}>
                Join our program, share your unique referral link, and earn up to 35% commission on every paid subscription you refer.
              </p>
              <div className={styles.featureGlow}></div>
            </div>
            
            <div className={styles.featureCard}>
              <h3 className={styles.featureTitle}>Why Become an Affiliate?</h3>
              <p className={styles.featureText}>
                Get recurring commissions, access to promotional materials, dedicated support, and early access to new features.
              </p>
              <div className={styles.featureCardFooter}>
                <a href="https://zenyai.promotekit.com" className={`${styles.button} ${styles.primaryButton}`}>
                Apply Now <span className={styles.arrow}>→</span>
                </a>
              </div>
            </div>
          </div>
        </section>
        
        <section className={styles.featureCards}>
          <div className={styles.featureCardsContainer}>
            <h2 className={styles.featureCardsTitle}>Affiliate Benefits</h2>
            <div className={styles.blogCardsGrid}>
              <div className={styles.blogCardLink}>
                <div className={styles.blogCard}>
                  <div className={styles.blogCardContent}>
                    <h3 className={styles.blogCardTitle}>35% Commission Rate</h3>
                    <p className={styles.blogCardSubtitle}>Earn generous commissions on all paid subscriptions referred through your unique link, with monthly payouts.</p>
                  </div>
                  <div className={`${styles.blogCardPattern} ${styles.blogCardPattern1}`}></div>
                  <div className={styles.blogCardDots}></div>
                  <div className={styles.blogGlassEffect}></div>
                  <div className={styles.blogCardOverlay}></div>
                </div>
              </div>
              
              <div className={styles.blogCardLink}>
                <div className={styles.blogCard}>
                  <div className={styles.blogCardContent}>
                    <h3 className={styles.blogCardTitle}>Marketing Resources</h3>
                    <p className={styles.blogCardSubtitle}>Access professionally designed banners, email templates, and promotional content to help drive conversions.</p>
                  </div>
                  <div className={`${styles.blogCardPattern} ${styles.blogCardPattern2}`}></div>
                  <div className={styles.blogCardLines}></div>
                  <div className={styles.blogGlassEffect}></div>
                  <div className={styles.blogCardOverlay}></div>
                </div>
              </div>
              
              <div className={styles.blogCardLink}>
                <div className={styles.blogCard}>
                  <div className={styles.blogCardContent}>
                    <h3 className={styles.blogCardTitle}>Dedicated Support</h3>
                    <p className={styles.blogCardSubtitle}>Get personalized support from our affiliate team to maximize your earnings and optimize your campaigns.</p>
                  </div>
                  <div className={`${styles.blogCardPattern} ${styles.blogCardPattern3}`}></div>
                  <div className={styles.blogCardDots}></div>
                  <div className={styles.blogGlassEffect}></div>
                  <div className={styles.blogCardOverlay}></div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className={styles.footerCTA}>
          <MovingGridBackground />
          <div className={styles.footerContent}>
            <div className={styles.footerTextContainer}>
              <h2 className={styles.footerTitle}>Start Earning Today</h2>
              <a href="https://zenyai.promotekit.com" className={`${styles.button} ${styles.primaryButton} ${styles.footerButton}`}>
                Join Our Affiliate Program
              </a>
            </div>
          </div>
        </section>
        
        <footer className={styles.footer}>
          <div className={styles.footerContainer}>
            <div className={styles.simplifiedFooter}>
              <a href="/terms" className={styles.footerLinkLarge}>Terms</a>
              <a href="/affiliate/faq" className={styles.footerLinkLarge}>FAQ</a>
              <a href="https://discord.gg/Ve9fyDUTev" className={styles.footerLinkLarge}>Discord</a>
              <a href="https://www.linkedin.com/company/zenyai/?viewAsMember=true" className={styles.footerLinkLarge}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

// Wrapper component to handle callback
const PromoNotificationWithCallback = ({ visible, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(visible);
  
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);
  
  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss();
  };
  
  return (
    <div className={isVisible ? "" : styles.hidden}>
      <PromoNotification 
        onDismiss={handleDismiss} 
      />
    </div>
  );
};

export default Affiliate; 